import React, { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { Atmosphere } from '@/components/shared/Atmosphere'
import { QUIZ_QUESTIONS } from '@/data/destinations'
import type { QuizAnswer } from '@/types'

interface Bubble {
  id: string
  role: 'ai' | 'user'
  content: string
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V']

// Passage's guide is measured — a short scenario-flavour line before each
// question, delivered slowly. Kept separate from the underlying question data
// so the DNA scoring / existing question text remain untouched.
const SCENARIO_LINES = [
  '',
  'Let us sit with that a moment.',
  'Consider this carefully — there is no wrong answer, only your answer.',
  'A question I ask only those who have truly lived a little.',
  'And now, the question that matters most.',
]

function ThinkingDots() {
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center', padding: '3px 0' }}>
      {[0, 1, 2].map((i) => (
        <motion.div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--gold)' }}
          animate={{ y: [0, -6, 0], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.24 }}
        />
      ))}
    </div>
  )
}

export function QuizScreen() {
  const { addAnswer, setScreen } = useAppStore()
  const [curQ, setCurQ] = useState(0)
  const [bubbles, setBubbles] = useState<Bubble[]>([])
  const [thinking, setThinking] = useState(false)
  const [showOpts, setShowOpts] = useState(false)
  const [inputVal, setInputVal] = useState('')
  const chatRef = useRef<HTMLDivElement>(null)

  const scrollBottom = () => chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' })

  const pushQ = useCallback((idx: number) => {
    if (idx >= QUIZ_QUESTIONS.length) return
    const q = QUIZ_QUESTIONS[idx]
    setThinking(true); setShowOpts(false)
    // Passage pacing: slow, measured — 1.3s of "thought" before the guide speaks.
    setTimeout(() => {
      setThinking(false)
      const scenario = SCENARIO_LINES[idx] ? `<em>${SCENARIO_LINES[idx]}</em><br/><br/>` : ''
      const content = q.question ? `${scenario}${q.intro}<br/><br/>${q.question}` : `${scenario}${q.intro}`
      setBubbles((prev) => [...prev, { id: `q-${idx}`, role: 'ai', content }])
      setShowOpts(true)
      setTimeout(scrollBottom, 100)
    }, 1300)
  }, [])

  useEffect(() => { pushQ(0) }, [pushQ])
  useEffect(() => { scrollBottom() }, [bubbles, thinking])

  const handleAnswer = (emoji: string, label: string) => {
    addAnswer({ emoji, label, questionIndex: curQ } as QuizAnswer)
    setShowOpts(false)
    setBubbles((prev) => [...prev, { id: `a-${curQ}`, role: 'user', content: `${emoji} ${label}` }])
    const next = curQ + 1
    if (next >= QUIZ_QUESTIONS.length) {
      setTimeout(() => {
        setThinking(true)
        setTimeout(() => {
          setThinking(false)
          setBubbles((prev) => [...prev, { id: 'done', role: 'ai', content: 'I have found your Travel DNA. Your destinations await — chosen with the care your experience deserves.' }])
          setTimeout(() => setScreen('dna'), 1800)
        }, 1100)
      }, 500)
    } else {
      setCurQ(next)
      setTimeout(() => pushQ(next), 600)
    }
  }

  // Free-text answers are stored as the user's answerLabel (for the AI prompt)
  // and mapped to the FIRST option of the current question for DNA scoring.
  const handleSend = () => {
    if (!inputVal.trim()) return
    const firstOption = QUIZ_QUESTIONS[curQ]?.options[0]
    const emoji = firstOption?.emoji || '✍'
    handleAnswer(emoji, inputVal.trim())
    setInputVal('')
  }

  const opts = showOpts && curQ < QUIZ_QUESTIONS.length ? QUIZ_QUESTIONS[curQ].options : []
  const progress = ((curQ + 1) / QUIZ_QUESTIONS.length) * 100

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      background: 'radial-gradient(ellipse at 50% 0%, #33200F 0%, #241209 60%, #180C05 100%)',
      position: 'relative',
    }}>
      <Atmosphere count={14} />
      <div style={{ position: 'relative', zIndex: 5 }}>
        <Topbar center={`${ROMAN[curQ] || 'V'} of V`} backTo="mode" />
      </div>

      {/* Segmented progress bar — 5 segments */}
      <div style={{ display: 'flex', gap: 3, padding: '0 0', height: 3, background: 'rgba(212,160,23,0.12)', position: 'relative', zIndex: 5 }}>
        {QUIZ_QUESTIONS.map((_, i) => (
          <div key={i} style={{ flex: 1, background: 'rgba(212,160,23,0.12)', position: 'relative', overflow: 'hidden' }}>
            <motion.div
              style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, var(--gold), var(--gold2))', transformOrigin: 'left' }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: i < curQ || (i === curQ && bubbles.some((b) => b.id === `a-${i}`)) ? 1 : i === curQ ? 0.5 : 0 }}
              transition={{ duration: 0.6 }}
            />
          </div>
        ))}
      </div>

      {/* Chat */}
      <div ref={chatRef} style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12, position: 'relative', zIndex: 5 }}>
        <AnimatePresence initial={false}>
          {bubbles.map((b) => (
            <motion.div key={b.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              style={{ display: 'flex', gap: 10, justifyContent: b.role === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-start' }}>
              {b.role === 'ai' && (
                <div style={{ width: 34, height: 34, background: 'rgba(212,160,23,0.1)', border: '1px solid var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--serif-caps)', fontSize: 12, color: 'var(--gold2)' }}>P</div>
              )}
              {b.role === 'ai' ? (
                <div className="p-bubble" style={{ background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(212,160,23,0.18)', padding: '13px 15px', maxWidth: '88%', fontFamily: 'var(--serif-body)', fontSize: 15, lineHeight: 1.8, color: 'var(--parch)' }}
                  dangerouslySetInnerHTML={{ __html: b.content }} />
              ) : (
                <div style={{ background: 'var(--gold)', color: '#241209', fontFamily: 'var(--serif-body)', fontSize: 15, padding: '10px 14px', maxWidth: '76%', border: '1px solid var(--gold2)' }}>
                  {b.content}
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {thinking && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ width: 34, height: 34, background: 'rgba(212,160,23,0.1)', border: '1px solid var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--serif-caps)', fontSize: 12, color: 'var(--gold2)' }}>P</div>
            <div style={{ background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(212,160,23,0.18)', padding: '13px 15px' }}>
              <ThinkingDots />
            </div>
          </motion.div>
        )}
      </div>

      {/* Options */}
      <AnimatePresence>
        {opts.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
            style={{ padding: '0 16px 8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, position: 'relative', zIndex: 5 }}>
            {opts.map((opt, i) => (
              <motion.button key={i} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.12, duration: 0.45 }}
                onClick={() => handleAnswer(opt.emoji, opt.label)}
                style={{
                  border: '1px solid rgba(212,160,23,0.22)', padding: '11px 12px',
                  fontFamily: 'var(--serif-body)', fontSize: 14, color: 'rgba(245,237,216,0.82)',
                  background: 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left',
                  display: 'flex', alignItems: 'flex-start', gap: 8, lineHeight: 1.4,
                  transition: 'border-color 0.4s, background 0.4s',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--gold)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(212,160,23,0.08)' }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(212,160,23,0.22)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.03)' }}
              >
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 2 }}>{opt.emoji}</span>
                <span>{opt.label}</span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Free-text input */}
      <div style={{ padding: '11px 14px', background: 'rgba(0,0,0,0.3)', borderTop: '1px solid rgba(212,160,23,0.18)', display: 'flex', gap: 8, position: 'relative', zIndex: 5 }}>
        <input value={inputVal} onChange={(e) => setInputVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Type freely, or tap a choice above…"
          style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(212,160,23,0.25)', padding: '9px 14px', fontFamily: 'var(--serif-body)', fontSize: 14, color: 'var(--parch)', outline: 'none' }} />
        <button onClick={handleSend} style={{ width: 38, height: 38, background: 'var(--gold)', border: 'none', cursor: 'pointer', color: '#241209', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>→</button>
      </div>
    </div>
  )
}
